import React, { useEffect, useState } from 'react'
import { SyncStatus } from '../lib/useSyncedDocument'

/**
 * Says where the plan currently lives.
 *
 * Worth the pixels because the app saves by itself: with no save button, the
 * only way to know a change reached the server is to be told. It stays quiet
 * when everything is fine and gets loud only when something needs a decision.
 */

function relative(at: number | null): string {
  if (at === null) return 'Saved'
  const seconds = Math.round((Date.now() - at) / 1000)
  if (seconds < 45) return 'Saved'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `Saved ${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `Saved ${hours}h ago`
  return `Saved ${Math.round(hours / 24)}d ago`
}

export function SyncPill({ status, onRetry }: { status: SyncStatus; onRetry: () => void }) {
  // "Saved 2m ago" that still says 2m an hour later is worse than no timestamp.
  const [, tick] = useState(0)
  useEffect(() => {
    if (status.kind !== 'saved') return
    const id = setInterval(() => tick((n) => n + 1), 30_000)
    return () => clearInterval(id)
  }, [status.kind])

  if (status.kind === 'conflict') return null

  const { label, tone, action } = describe(status)

  return (
    <span className="flex items-center gap-1.5 text-xs text-ink/70 whitespace-nowrap">
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${tone}`} aria-hidden />
      <span className="hidden sm:inline">{label}</span>
      {action && (
        <button onClick={onRetry} className="text-accent hover:underline">
          Retry
        </button>
      )}
    </span>
  )

  function describe(s: SyncStatus): { label: string; tone: string; action: boolean } {
    switch (s.kind) {
      case 'saving':
        return { label: 'Saving…', tone: 'bg-ink/30', action: false }
      case 'pending':
        return { label: 'Unsaved changes', tone: 'bg-ink/30', action: false }
      case 'offline':
        return { label: 'Offline — will retry', tone: 'bg-rag-medium', action: true }
      case 'rejected':
        return { label: "Couldn't save", tone: 'bg-rag-bad', action: true }
      case 'conflict':
        // Unreachable — the early return above covers it. Listed so the
        // compiler can prove the remaining case is 'saved'.
        return { label: 'Changed elsewhere', tone: 'bg-rag-medium', action: false }
      default:
        return { label: relative(s.at), tone: 'bg-rag-good', action: false }
    }
  }
}

/**
 * Shown when the server has a newer version than this tab started from —
 * another device, or a second tab. There is no safe automatic answer here, so
 * it asks, and says what each choice throws away.
 */
export function ConflictBar({
  status,
  onKeepMine,
  onTakeTheirs,
}: {
  status: SyncStatus
  onKeepMine: () => void
  onTakeTheirs: () => void
}) {
  if (status.kind !== 'conflict') return null

  const savedAt = status.theirs?.updatedAt ? new Date(status.theirs.updatedAt) : null

  return (
    <div className="bg-rag-mediumBg border-b border-rag-medium/30 px-4 sm:px-6 py-3">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <p className="text-sm text-ink flex-1">
          <strong className="font-medium">This plan changed somewhere else</strong>
          {savedAt && (
            <span className="text-ink/75">
              {' '}
              — saved at {savedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} on another
              device or tab.
            </span>
          )}
        </p>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={onTakeTheirs}
            className="px-3 py-1.5 text-xs rounded-sm border border-line bg-white text-ink hover:bg-paper"
          >
            Use that version
          </button>
          <button
            onClick={onKeepMine}
            className="px-3 py-1.5 text-xs rounded-sm bg-accent text-white hover:brightness-110"
          >
            Keep what's on screen
          </button>
        </div>
      </div>
      <p className="text-xs text-ink/70 mt-1.5">
        Whichever you don't pick is discarded — the two aren't merged.
      </p>
    </div>
  )
}
